import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useEventListener } from 'ahooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, find, filter } from 'lodash-es';
import { message } from 'antd';
import posthog from '@leyan/lytics';

import type { TypeSource } from './NewTodo';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import { parseMustache } from './TemplateNode';
import { updateTodoUserConfig } from './api';
import { isMac } from 'utils/is';
import { queries } from 'services/queries';

/** 回复推荐区域所需表单字段，由调用方从 Formik 或其它来源注入，实现与 useFormikContext 解耦 */
export type ReplyMsgsFormValues = Pick<
  Todo.TodoInstance,
  'id' | 'region' | 'type' | 'operateResult'
>;

/**
 * 回复推荐区域业务逻辑：模板筛选、键盘快捷键、展示原文配置、激活项与滚动。
 * 单一职责：仅负责状态与副作用，不包含 UI。
 * @param typeSource 类型源数据，由调用方从 TypeSourceContext 或其它数据源注入
 * @param formValues 表单字段，由调用方从 useFormikContext 或其它来源注入
 */
export function useReplyMsgs(typeSource: TypeSource, formValues: ReplyMsgsFormValues) {
  const { t } = useTranslate();
  const { id, region, type, operateResult } = formValues;
  const { getResultTypeLabel } = useResultTypeLabel();

  const filterSelfTemplate = useMemo(() => {
    const target = find(typeSource, (e) => e.type === type);
    return find(
      get(target, 'origin.templates', []),
      (e) =>
        !e.parentResultType && e.resultType === operateResult && e.region === region && e.enable,
    );
  }, [operateResult, type, typeSource, region]);

  const isEmpty = useMemo(() => {
    if (!filterSelfTemplate) return true;
    return parseMustache(filterSelfTemplate.executeContext.content, t)?.isEmpty();
  }, [filterSelfTemplate, t]);

  const filterTemplates = useMemo(() => {
    const target = find(typeSource, (e) => e.type === type);
    return filter(
      get(target, 'origin.templates', []),
      (e) =>
        !!e.parentResultType &&
        e.parentResultType === operateResult &&
        e.region === region &&
        e.enable,
    );
  }, [typeSource, operateResult, type, region]);

  const containerRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filterSelfTemplate, filterTemplates]);

  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [operateResult]);

  useEventListener('keydown', (e) => {
    const { key, altKey, metaKey } = e;
    if (isMac ? metaKey : altKey) {
      if (filterTemplates.length) {
        let nextActiveIndex = -1;
        if (key === 'ArrowUp' && activeIndex > 0) {
          nextActiveIndex = activeIndex - 1;
          posthog.capture('$hotKey', {
            hotKey: `${isMac ? 'Cmd' : 'Alt'}+ArrowUp`,
            todoId: id,
          });
        } else if (key === 'ArrowDown' && activeIndex < filterTemplates.length - 1) {
          nextActiveIndex = activeIndex + 1;
          posthog.capture('$hotKey', {
            hotKey: `${isMac ? 'Cmd' : 'Alt'}+ArrowDown`,
            todoId: id,
          });
        } else if (filterTemplates[Number(key) - 1]) {
          nextActiveIndex = Number(key) - 1;
        }
        if (nextActiveIndex > -1) {
          e.preventDefault();
          setActiveIndex(nextActiveIndex);
          templateRef.current?.children[nextActiveIndex]?.scrollIntoView({ behavior: 'smooth' });
          posthog.capture('$hotKey', {
            hotKey: `${isMac ? 'Cmd' : 'Alt'}+${nextActiveIndex}`,
            todoId: id,
          });
        }
      }
    }
  });

  const { data, refetch } = useQuery({
    ...queries.tickets.userConfig,
    staleTime: 1000 * 60 * 60,
  });
  const showOriginalText = data?.data?.showOriginalText ?? false;

  const { mutateAsync } = useMutation({
    mutationFn: (data: boolean) => updateTodoUserConfig({ showOriginalText: data }),
    onSuccess: () => refetch(),
    onError: () => {
      message.error(t('common.feedback.operationFailed'));
    },
  });

  const shouldRender = !(isEmpty && !filterTemplates.length);
  const resultTypeLabel = getResultTypeLabel(operateResult);

  return {
    shouldRender,
    containerRef,
    templateRef,
    filterSelfTemplate: filterSelfTemplate ?? null,
    filterTemplates,
    activeIndex,
    setActiveIndex,
    showOriginalText,
    onShowOriginalTextChange: mutateAsync,
    resultTypeLabel,
  };
}
